// SCR-STAFF-412 — مغایرت‌گیری درگاه و کارتخوان (US-BIL-130؛ G-01): بارگذاری فایل تسویه بانک/PSP (CSV: reference,amount,fee,transaction_date,
// settlement_date)، تطبیق خودکار با پرداخت‌های ققنوس، سه دسته «منطبق / فقط در بانک / فقط در ققنوس» و رفع هر ردیف باز با دلیل (پیوند به پرداخت،
// اصلاح، پیگیری با بانک). کارمزد و خالص تسویه در رویداد billing.psp.settled.v1 به ACC و CMP می‌رسد.
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type BilReconciliationBatchView,
  type BilReconciliationItemView,
  type BilReconciliationMatch,
  type BilReconciliationResolution,
  type BilSettlementChannel,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useActiveBranch } from '../scheduling/common';
import { Amount, bilKeys, SCR_STAFF_412 } from './common';

const CHANNELS: readonly BilSettlementChannel[] = ['CardPos', 'OnlineGateway'];
const RESOLUTIONS: readonly Exclude<BilReconciliationResolution, 'Open'>[] = ['LinkedPayment', 'Corrected', 'FollowUpWithBank'];

const matchTone = (match: BilReconciliationMatch) => (match === 'Matched' ? 'done' : match === 'OnlyInBank' ? 'error' : 'pending');

export function ReconciliationPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const { branchId: activeBranch } = useActiveBranch();
  const branches = activeTenant?.branches ?? [];
  const [branchChoice, setBranchId] = useState('');
  const branchId = branchChoice || activeBranch || branches[0]?.branchId || '';
  const [channel, setChannel] = useState<BilSettlementChannel>('CardPos');
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState<{ name: string; content: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resolving, setResolving] = useState<BilReconciliationItemView | null>(null);
  const [resolution, setResolution] = useState<Exclude<BilReconciliationResolution, 'Open'>>('FollowUpWithBank');
  const [paymentId, setPaymentId] = useState('');
  const [note, setNote] = useState('');
  const canReconcile = access.accessMode === 'Full' && access.can('bil.psp.reconcile');
  const batches = useQuery({
    queryKey: [...bilKeys.reconciliations(session?.tenantId), branchId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/reconciliations', { params: { query: { branchId: branchId || undefined } } })),
  });
  const accounts = useQuery({
    queryKey: bilKeys.bankAccounts(session?.tenantId, branchId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/bank-accounts', { params: { query: { branchId: branchId || undefined } } })),
    enabled: branchId !== '',
  });
  const detail = useQuery({
    queryKey: bilKeys.reconciliation(session?.tenantId, selectedId ?? ''),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/reconciliations/{id}', { params: { path: { id: selectedId ?? '' } } })),
    enabled: selectedId !== null,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  const upload = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/reconciliations', {
          body: { branchId, channel, bankAccountId: accountId, fileName: file?.name ?? null, content: file?.content ?? null, lines: null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (batch) => {
      setFile(null);
      setSelectedId(batch.id);
      refresh();
    },
  });
  const resolve = useMutation({
    mutationFn: ({ batch, item }: { batch: string; item: BilReconciliationItemView }) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/reconciliations/{id}/items/{itemId}:resolve', {
          params: { path: { id: batch, itemId: item.id } },
          body: { resolution, paymentId: resolution === 'LinkedPayment' ? paymentId.trim() || null : null, note: note.trim() },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setResolving(null);
      setPaymentId('');
      setNote('');
      refresh();
    },
  });
  const batchColumns: DataTableColumn<BilReconciliationBatchView>[] = [
    { id: 'source', headerKey: 'bil:reconciliation.file', cell: (row) => <bdi dir="ltr">{row.source}</bdi> },
    { id: 'channel', headerKey: 'bil:reconciliation.channel', cell: (row) => t(`bil:reconciliation.channels.${row.channel}`) },
    {
      id: 'period',
      headerKey: 'bil:reconciliation.period',
      cell: (row) => (
        <>
          <JalaliDate value={row.periodFrom} /> – <JalaliDate value={row.periodTo} />
        </>
      ),
    },
    { id: 'matched', headerKey: 'bil:reconciliation.match.Matched', cell: (row) => faDigits(String(row.matched)) },
    { id: 'bank', headerKey: 'bil:reconciliation.match.OnlyInBank', cell: (row) => faDigits(String(row.onlyInBank)) },
    { id: 'phoenix', headerKey: 'bil:reconciliation.match.OnlyInPhoenix', cell: (row) => faDigits(String(row.onlyInPhoenix)) },
    { id: 'open', headerKey: 'bil:reconciliation.open', cell: (row) => faDigits(String(row.open)) },
    { id: 'fee', headerKey: 'bil:reconciliation.fee', cell: (row) => <Amount money={row.settledFee} /> },
    {
      id: 'actions',
      headerKey: 'bil:deposits.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setSelectedId(row.id)} data-testid="bil-recon-open">
          {t('bil:installments.details')}
        </Button>
      ),
    },
  ];
  const itemColumns: DataTableColumn<BilReconciliationItemView>[] = [
    { id: 'match', headerKey: 'bil:reconciliation.result', cell: (row) => <StatusChip tone={matchTone(row.match)} labelKey={`bil:reconciliation.match.${row.match}`} /> },
    { id: 'reference', headerKey: 'bil:reconciliation.reference', cell: (row) => <bdi dir="ltr">{row.bankReference ?? row.phoenixReference ?? '—'}</bdi> },
    { id: 'bank_amount', headerKey: 'bil:reconciliation.bank_amount', cell: (row) => (row.bankAmount ? <Amount money={row.bankAmount} /> : '—') },
    { id: 'phoenix_amount', headerKey: 'bil:reconciliation.phoenix_amount', cell: (row) => (row.phoenixAmount ? <Amount money={row.phoenixAmount} /> : '—') },
    { id: 'fee', headerKey: 'bil:reconciliation.fee', cell: (row) => <Amount money={row.bankFee} /> },
    { id: 'settled', headerKey: 'bil:reconciliation.settlement_date', cell: (row) => (row.settlementDate ? <JalaliDate value={row.settlementDate} /> : '—') },
    { id: 'resolution', headerKey: 'bil:deposits.columns.status', cell: (row) => t(`bil:reconciliation.resolution.${row.resolution}`) },
    {
      id: 'actions',
      headerKey: 'bil:deposits.columns.actions',
      cell: (row) =>
        canReconcile && row.match !== 'Matched' && row.resolution === 'Open' ? (
          <Button size="small" onClick={() => setResolving(row)} data-testid="bil-recon-resolve">
            {t('bil:reconciliation.resolve')}
          </Button>
        ) : null,
    },
  ];
  const readFile = (input: HTMLInputElement) => {
    const chosen = input.files?.[0];
    if (!chosen) {
      setFile(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: chosen.name, content: typeof reader.result === 'string' ? reader.result : '' });
    reader.readAsText(chosen);
  };
  const batch = detail.data;
  return (
    <ListPage screenId={SCR_STAFF_412} titleKey="bil:reconciliation.title" subtitleKey="bil:reconciliation.subtitle">
      {canReconcile ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField select size="small" label={t('bil:registers.branch')} value={branchId} onChange={(event) => setBranchId(event.target.value)} sx={{ minWidth: 180 }}>
              {branches.map((branch) => (
                <MenuItem key={branch.branchId} value={branch.branchId}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label={t('bil:reconciliation.channel')} value={channel} onChange={(event) => setChannel(event.target.value as BilSettlementChannel)} sx={{ minWidth: 160 }}>
              {CHANNELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`bil:reconciliation.channels.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label={t('bil:bank_deposit.account')}
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              sx={{ minWidth: 220 }}
              slotProps={{ htmlInput: { 'data-testid': 'bil-recon-account' } }}
            >
              {(accounts.data?.items ?? []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.title} <bdi dir="ltr">{item.maskedNumber}</bdi>
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" component="label">
              {file ? file.name : t('bil:reconciliation.choose_file')}
              <input hidden type="file" accept=".csv,text/csv,text/plain" data-testid="bil-recon-file" onChange={(event) => readFile(event.currentTarget)} />
            </Button>
            <Button variant="contained" disabled={!file || accountId === '' || branchId === '' || upload.isPending} onClick={() => upload.mutate()} data-testid="bil-recon-upload">
              {t('bil:reconciliation.upload')}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('bil:reconciliation.format_help')}
          </Typography>
        </Paper>
      ) : null}
      <DataTable
        captionKey="bil:reconciliation.batches"
        columns={batchColumns}
        rows={batches.data?.items}
        getRowId={(row) => row.id}
        isLoading={batches.isPending}
        error={batches.error ?? upload.error}
        onRetry={() => void batches.refetch()}
        emptyTitleKey="bil:reconciliation.empty"
      />
      {batch ? (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }} data-testid="bil-recon-detail">
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('bil:reconciliation.summary')}: {t('bil:reconciliation.gross')} <Amount money={batch.settledGross} /> · {t('bil:reconciliation.fee')} <Amount money={batch.settledFee} />
          </Typography>
          <DataTable
            captionKey="bil:reconciliation.items"
            columns={itemColumns}
            rows={batch.items}
            getRowId={(row) => row.id}
            isLoading={false}
            error={resolve.error}
            emptyTitleKey="bil:reconciliation.empty"
          />
        </Paper>
      ) : null}
      <FormDialog
        open={resolving !== null}
        titleKey="bil:reconciliation.resolve"
        onSubmit={() => batch && resolving && note.trim() !== '' && resolve.mutate({ batch: batch.id, item: resolving })}
        onClose={() => setResolving(null)}
        isDirty={note !== ''}
        isSubmitting={resolve.isPending}
        submitError={resolve.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField select label={t('bil:reconciliation.resolution_label')} value={resolution} onChange={(event) => setResolution(event.target.value as Exclude<BilReconciliationResolution, 'Open'>)}>
            {RESOLUTIONS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:reconciliation.resolution.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {resolution === 'LinkedPayment' ? (
            <TextField required label={t('bil:reconciliation.payment_id')} value={paymentId} onChange={(event) => setPaymentId(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr' } }} />
          ) : null}
          <TextField required label={t('bil:write_off.reason')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'bil-recon-note' } }} />
        </Stack>
      </FormDialog>
    </ListPage>
  );
}
